import React from "react";
import { Link } from "react-router-dom";
import { Card, Container, Group, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import {
  IconArrowRight,
  IconChecklist,
  IconDeviceVisionPro,
  IconDeviceDesktop,
  IconSparkles,
} from "@tabler/icons-react";

import "./VersionsPage.scss";
import ReleaseBadges from "./ReleaseBadges";
import VersionControlCard from "./VersionControlCard";
import { useReleaseNotes } from "./useReleaseNotes";
import {
  currentDesktop,
  displayVersion,
  latestRelease,
  releaseCopy,
  releasePath,
  stripOffline,
} from "./releaseCatalog";

export default function VersionsPage() {
  const { t } = useTranslation();
  const { releases: releaseNotes, latestOffline } = useReleaseNotes();
  const latestPlatform = latestRelease(releaseNotes, false);
  const desktop = currentDesktop(latestOffline);
  const releaseStats = [
    {
      title: "Versions Covered",
      value: releaseNotes.length.toString(),
      icon: IconChecklist,
    },
    {
      title: t("releaseNotes.headsetCard"),
      value: latestPlatform?.version ?? "-",
      icon: IconSparkles,
    },
    {
      title: t("releaseNotes.desktopCard"),
      value: desktop ? stripOffline(desktop.version) : "-",
      icon: IconDeviceDesktop,
    },
    {
      title: "Headset + Web",
      value: "Unified",
      icon: IconDeviceVisionPro,
    },
  ];

  const latestHeroImage = latestPlatform?.heroImage;

  return (
    <div className="release-page">
      <Container size="xl" py="xl">
        <Stack gap="xl">
          {latestHeroImage ? (
            <section className="release-hero">
              <img
                src={latestHeroImage.url}
                alt={latestHeroImage.alt}
                className="release-hero__image"
              />
            </section>
          ) : null}

          <Group gap="md" className="release-hero__stats">
            {releaseStats.map((stat) => (
              <Card key={stat.title} className="release-stat" radius="lg" padding="md">
                <Group gap="sm" wrap="nowrap" align="flex-start">
                  <ThemeIcon variant="light" size={42} radius="md" color="blue">
                    <stat.icon size={22} />
                  </ThemeIcon>
                  <div>
                    <Text className="release-stat__value">{stat.value}</Text>
                    <Text className="release-stat__label">{stat.title}</Text>
                  </div>
                </Group>
              </Card>
            ))}
          </Group>

          <VersionControlCard />

          <section className="release-list">
            <Stack gap="md">
              {releaseNotes.map((release) => (
                <Card
                  key={release.version}
                  className="release-index-card"
                  radius="xl"
                  padding="lg"
                  component={Link}
                  to={releasePath(release)}
                >
                  <Group justify="space-between" wrap="nowrap" align="flex-start">
                    <div className="release-index-card__body">
                      <Group gap="sm" mb="xs">
                        <Title order={3}>{displayVersion(release)}</Title>
                        <ReleaseBadges release={release} current={desktop} downloadHint />
                      </Group>
                      <Text className="release-card__summary">
                        {releaseCopy(release, t).summary}
                      </Text>
                    </div>
                    <ThemeIcon variant="light" color="blue" radius="xl" size={36}>
                      <IconArrowRight size={18} />
                    </ThemeIcon>
                  </Group>
                </Card>
              ))}
            </Stack>
          </section>
        </Stack>
      </Container>
    </div>
  );
}
